import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { router } from '../router.js';
import { resolveRouterPath } from '../router.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

@customElement('brainstorm-sidebar')
export class BrainstormSidebar extends LitElement {
  @property({ type: Boolean }) visible: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    // Re-render when route changes to update active states
    router.addEventListener('route-changed', () => {
      this.requestUpdate();
    });
  }

  static styles = css`
    :host {
      display: none;
      position: fixed;
      right: 0; /* Flush to right edge */
      top: 60px; /* Start after header (60px height) */
      bottom: 36px; /* End before footer (36px height) */
      background: var(--background); /* Same as header and footer */
      border-left: 1px solid var(--border);
      z-index: 10; /* Lower than nav (100) and footer (100) */
      font-family: var(--font-mono);
      direction: rtl;
      width: 48px; /* Thinner sidebar */
      transition: width 0.2s ease;
    }

    :host([visible]) {
      display: block;
    }

    .sidebar-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 0;
      gap: 0;
    }

    .nav-group {
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      margin: 0;
    }

    .nav-group:not(:last-child) {
      border-bottom: 1px solid var(--border);
    }

    .nav-item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 0; /* 12px top/bottom, 0 left/right */
      cursor: pointer;
      border-radius: var(--radius);
      border: none; /* No border */
      transition: all 0.2s ease;
      position: relative;
      background: transparent;
      color: var(--muted-foreground);
    }

    .nav-item:hover {
      background: var(--accent);
      color: var(--foreground);
    }

    .nav-item.active {
      background: var(--primary);
      color: var(--background);
    }

    .nav-item sl-icon {
      font-size: 1.3rem;
      padding: 0;
      margin: 0;
    }

    .nav-item sl-icon::part(base) {
      padding: 0;
    }

    .nav-label {
      position: absolute;
      left: 70px;
      background: var(--foreground);
      color: var(--background);
      padding: 6px 12px;
      border-radius: var(--radius);
      font-size: 0.85rem;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      z-index: 1000;
    }

    .nav-item:hover .nav-label {
      opacity: 1;
    }

    .nav-label::after {
      content: '';
      position: absolute;
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid var(--foreground);
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
    }

    @media (max-width: 768px) {
      :host {
        display: none;
      }
    }
  `;

  handleNavigation(path: string) {
    router.navigate(resolveRouterPath(path));
  }

  handleNewTitle() {
    // Navigate to brainstorm page and trigger dialog via URL state
    const path = window.location.pathname;
    if (!path.includes('/brainstorm')) {
      // If not on brainstorm page, navigate there first
      router.navigate(resolveRouterPath('brainstorm'));
      // Wait for navigation, then dispatch event
      setTimeout(() => {
        this.dispatchEvent(new CustomEvent('new-title-request', { bubbles: true, composed: true }));
      }, 100);
    } else {
      // Already on brainstorm page, just dispatch event
      this.dispatchEvent(new CustomEvent('new-title-request', { bubbles: true, composed: true }));
    }
  }

  render() {
    const path = window.location.pathname;

    // Determine active state based on URL only
    const isCategoriesActive = path === '/brainstorm' || path === '/brainstorm/';
    const isAllTitlesActive = path.includes('/brainstorm/all-titles');
    const isCalendarActive = path.includes('/calendar');

    return html`
      <div class="sidebar-content">
        <!-- Brainstorm Group -->
        <div class="nav-group">
          <div
            class="nav-item ${isCategoriesActive ? 'active' : ''}"
            @click=${() => this.handleNavigation('brainstorm')}
          >
            <sl-icon name="grid-3x3-gap"></sl-icon>
            <span class="nav-label">الفئات</span>
          </div>

          <div
            class="nav-item ${isAllTitlesActive ? 'active' : ''}"
            @click=${() => this.handleNavigation('brainstorm/all-titles')}
          >
            <sl-icon name="list-ul"></sl-icon>
            <span class="nav-label">كل العناوين</span>
          </div>
        </div>

        <!-- Calendar Group -->
        <div class="nav-group">
          <div
            class="nav-item ${isCalendarActive ? 'active' : ''}"
            @click=${() => this.handleNavigation('calendar')}
          >
            <sl-icon name="${isCalendarActive ? 'calendar-fill' : 'calendar'}"></sl-icon>
            <span class="nav-label">التقويم</span>
          </div>
        </div>
      </div>
    `;
  }
}
